/**
 * Match service - API calls for the Three-Way Matching Engine module
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  Match,
  MatchListFilters,
  MatchListResponse,
  MatchStatus,
  GenerateMatchRequest,
  GenerateMatchResponse,
  MatchOutreachResult,
  OutreachRecipientRole,
} from '../types/match';

export async function fetchMatches(
  filters?: MatchListFilters
): Promise<ApiResponse<MatchListResponse>> {
  try {
    const response = await api.get('/matches', { params: filters });
    const payload = response.data;

    if (Array.isArray(payload)) {
      return {
        data: {
          matches: payload,
          total: payload.length,
          page: 1,
          size: payload.length,
          pages: 1,
        },
      };
    }

    return {
      data: {
        matches: payload?.matches || payload?.items || [],
        total: payload?.total ?? 0,
        page: payload?.page ?? 1,
        size: payload?.size ?? 20,
        pages: payload?.pages ?? 1,
      },
    };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load matches' };
  }
}

export async function fetchMatch(id: number): Promise<ApiResponse<Match>> {
  try {
    const response = await api.get(`/matches/${id}`);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load match' };
  }
}

export async function generateMatches(
  req: GenerateMatchRequest
): Promise<ApiResponse<GenerateMatchResponse>> {
  try {
    const response = await api.post('/matches/generate', req);
    const payload = response.data;
    if (Array.isArray(payload)) {
      return { data: { matches: payload, generated: payload.length } };
    }
    return {
      data: {
        matches: payload?.matches || [],
        generated: payload?.generated ?? (payload?.matches?.length || 0),
        skipped_reason: payload?.skipped_reason,
        completion_pct: payload?.completion_pct,
        completion_threshold: payload?.completion_threshold,
      },
    };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to generate matches' };
  }
}

export async function updateMatchStatus(
  id: number,
  status: MatchStatus,
  reviewedBy?: string
): Promise<ApiResponse<Match>> {
  try {
    const body: Record<string, any> = { status };
    if (reviewedBy) body.reviewed_by = reviewedBy;
    body.reviewed_at = new Date().toISOString();
    const response = await api.patch(`/matches/${id}`, body);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to update match' };
  }
}

export async function requestMatchReview(
  id: number
): Promise<ApiResponse<{ match_id: number; queued: boolean; message: string }>> {
  try {
    const response = await api.post(`/matches/${id}/review`);
    return { data: response.data };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to schedule match review',
    };
  }
}

export async function sendMatchOutreach(
  id: number,
  body?: { recipient_role?: OutreachRecipientRole; override_recipient_email?: string }
): Promise<ApiResponse<MatchOutreachResult>> {
  try {
    const response = await api.post(`/matches/${id}/outreach`, body || {});
    return { data: response.data };
  } catch (error: any) {
    // The endpoint returns 4xx detail as either a string or an object with
    // {sent, reason, ...}. Surface either form.
    const detail = error.response?.data?.detail;
    let message: string;
    if (typeof detail === 'string') {
      message = detail;
    } else if (detail?.reason) {
      message = `Outreach blocked: ${detail.reason}`;
    } else {
      message = 'Failed to send match outreach';
    }
    return { error: message };
  }
}

export async function requestMatchIntro(
  id: number
): Promise<ApiResponse<{
  ok: boolean;
  match_id: number;
  pipeline_created: boolean;
  notification_sent: boolean;
}>> {
  try {
    const response = await api.post(`/matches/${id}/request-intro`);
    return { data: response.data };
  } catch (error: any) {
    const detail = error.response?.data?.detail;
    let message: string;
    if (typeof detail === 'string') {
      message = detail;
    } else if (detail?.reason === 'already_sent') {
      message = 'Introduction already requested.';
    } else if (detail?.reason) {
      message = `Request blocked: ${detail.reason}`;
    } else {
      message = 'Failed to request introduction';
    }
    return { error: message };
  }
}

export async function deleteMatch(
  id: number
): Promise<ApiResponse<{ message: string }>> {
  try {
    const response = await api.delete(`/matches/${id}`);
    return { data: response.data };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to delete match' };
  }
}

export async function fetchInvestorMatches(
  investorId: number
): Promise<ApiResponse<Match[]>> {
  try {
    const response = await api.get(`/investors/${investorId}/matches`);
    const payload = response.data;
    return {
      data: Array.isArray(payload) ? payload : payload?.matches || [],
    };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load investor matches' };
  }
}

export async function fetchProjectMatches(
  projectId: number
): Promise<ApiResponse<Match[]>> {
  try {
    const response = await api.get(`/projects/${projectId}/matches`);
    const payload = response.data;
    return {
      data: Array.isArray(payload) ? payload : payload?.matches || [],
    };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load project matches' };
  }
}

export async function fetchCompanyMatches(
  companyId: number
): Promise<ApiResponse<Match[]>> {
  try {
    // No dedicated /companies/{id}/matches route — use the list endpoint
    // filtered by startup_id (companies and startups are the same entity here).
    const response = await api.get('/matches', {
      params: { startup_id: companyId, size: 500 },
    });
    const payload = response.data;
    if (Array.isArray(payload)) return { data: payload };
    return { data: payload?.matches || payload?.items || [] };
  } catch (error: any) {
    return { error: error.response?.data?.detail || 'Failed to load company matches' };
  }
}

const matchService = {
  fetchMatches,
  fetchMatch,
  generateMatches,
  updateMatchStatus,
  requestMatchReview,
  requestMatchIntro,
  sendMatchOutreach,
  deleteMatch,
  fetchInvestorMatches,
  fetchProjectMatches,
  fetchCompanyMatches,
};

export default matchService;
